import {
  AlertDialog,
  AlertDialogBody,
  AlertDialogContent,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogOverlay,
  Button,
  useDisclosure,
} from "@chakra-ui/react";
import React from "react";

/**
 * Campo con etiqueta + botón que abre una URL externa en nueva pestaña,
 * previo diálogo de confirmación (salida de la app en otra pestaña).
 *
 * @param {object} props
 * @param {string} props.fieldLabel — Etiqueta visible (ej. «PDF»)
 * @param {string|null|undefined} props.url
 */
export default function ExternalLinkField({ fieldLabel, url }) {
  const { isOpen, onOpen, onClose } = useDisclosure();
  const cancelRef = React.useRef(null);

  const safeUrl =
    typeof url === "string" && url.trim() !== "" ? url.trim() : null;

  let hostname = "";
  if (safeUrl) {
    try {
      hostname = new URL(safeUrl).hostname;
    } catch {
      hostname = "";
    }
  }

  const openConfirmed = React.useCallback(() => {
    if (!safeUrl) return;
    window.open(safeUrl, "_blank", "noopener,noreferrer");
    onClose();
  }, [safeUrl, onClose]);

  return (
    <div className="min-w-0">
      <div className="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
        {fieldLabel}
      </div>
      <div className="mt-2 flex flex-col gap-2">
        {safeUrl ? (
          <>
            <Button
              size="sm"
              colorScheme="brand"
              variant="outline"
              onClick={onOpen}
              className="w-fit max-w-full"
            >
              Abrir en nueva pestaña
            </Button>
            <p
              className="break-all font-mono text-[11px] leading-snug text-gray-500 dark:text-gray-400"
              title={safeUrl}
            >
              {safeUrl}
            </p>
          </>
        ) : (
          <span className="text-sm text-navy-800 dark:text-gray-100">—</span>
        )}
      </div>

      {safeUrl ? (
        <AlertDialog
          isOpen={isOpen}
          leastDestructiveRef={cancelRef}
          onClose={onClose}
          isCentered
        >
          <AlertDialogOverlay bg="blackAlpha.400" backdropFilter="blur(4px)" />
          <AlertDialogContent className="font-dm dark:bg-navy-800 dark:text-white">
            <AlertDialogHeader
              fontSize="lg"
              fontWeight="bold"
              className="text-navy-700 dark:text-white"
            >
              Salir a un enlace externo
            </AlertDialogHeader>
            <AlertDialogBody className="text-sm text-gray-600 dark:text-gray-300">
              Se abrirá una <strong>nueva pestaña</strong> del navegador
              {hostname ? (
                <>
                  {" "}
                  hacia <strong>{hostname}</strong>
                </>
              ) : null}
              . Esta aplicación (DTE↔NRS) seguirá abierta en esta pestaña.
            </AlertDialogBody>
            <AlertDialogFooter className="gap-2">
              <Button ref={cancelRef} variant="ghost" onClick={onClose}>
                Cancelar
              </Button>
              <Button colorScheme="brand" onClick={openConfirmed}>
                Abrir enlace
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      ) : null}
    </div>
  );
}
